import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Heart, Shield, Globe, Users } from 'lucide-react';
import { UKFlag } from './HeroSection';

const VOLUNTEERS_IMAGE = "https://customer-assets.emergentagent.com/job_liquid-dignity/artifacts/z5uxppnv_WhatsApp%20Image%202026-03-14%20at%2018.11.48%20%282%29.jpeg";

const content = {
  en: {
    eyebrow: 'OUR MISSION',
    headline: 'Hope Through Water',
    subheadline: 'We deliver more than water. We deliver dignity, hope, and a future.',
    pillars: [
      {
        icon: Heart,
        title: 'Compassion',
        description: 'Every project begins with understanding communities and their unique needs.',
      },
      {
        icon: Shield,
        title: 'British Trust',
        description: 'UK-registered charity with complete transparency in every pound spent.',
      },
      {
        icon: Globe,
        title: 'Global Reach',
        description: 'From Africa to Asia, we go where the need is greatest.',
      },
      {
        icon: Users,
        title: 'Community',
        description: 'Local teams trained to maintain water systems for generations.',
      },
    ],
    quote: '"Water is not charity. It is the foundation of human dignity."',
  },
  es: {
    eyebrow: 'NUESTRA MISIÓN',
    headline: 'Esperanza a Través del Agua',
    subheadline: 'Entregamos más que agua. Entregamos dignidad, esperanza y un futuro.',
    pillars: [
      {
        icon: Heart,
        title: 'Compasión',
        description: 'Cada proyecto comienza con entender a las comunidades y sus necesidades únicas.',
      },
      {
        icon: Shield,
        title: 'Confianza Británica',
        description: 'Caridad registrada en UK con total transparencia en cada libra gastada.',
      },
      {
        icon: Globe,
        title: 'Alcance Global',
        description: 'De África a Asia, vamos donde la necesidad es mayor.',
      },
      {
        icon: Users,
        title: 'Comunidad',
        description: 'Equipos locales capacitados para mantener sistemas de agua por generaciones.',
      },
    ],
    quote: '"El agua no es caridad. Es la base de la dignidad humana."',
  },
};

export const MissionSection = ({ language }) => {
  const t = content[language];
  const ref = useRef(null);
  
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"]
  });
  
  const imageY = useTransform(scrollYProgress, [0, 1], [50, -50]);

  return (
    <section 
      id="mission" 
      ref={ref}
      className="relative py-32 overflow-hidden"
      data-testid="mission-section"
    >
      {/* Seamless gradient - NO CUTS */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A1F3A] via-[#0F1E30] to-[#0A1F3A]" />

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/5 border border-white/10 mb-6">
            <UKFlag className="w-5 h-3" />
            <span className="text-amber-300/80 text-xs font-medium tracking-widest">{t.eyebrow}</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-light text-white/90 mb-4">
            {t.headline}
          </h2>
          <p className="text-lg text-white/50 max-w-xl mx-auto font-light">
            {t.subheadline}
          </p>
        </motion.div>

        {/* Content Grid */}
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left: Image with Parallax */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative"
          >
            <motion.div 
              style={{ y: imageY }}
              className="relative aspect-[4/3] rounded-3xl overflow-hidden"
            >
              <img 
                src={VOLUNTEERS_IMAGE}
                alt="Hydro Life Volunteers"
                className="w-full h-full object-cover object-center"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A1F3A]/60 to-transparent" />
            </motion.div>
            
            {/* Floating badge */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: 0.3 }}
              className="absolute -bottom-4 -right-4 bg-gradient-to-br from-amber-500 to-yellow-400 rounded-2xl px-6 py-4 shadow-xl"
            >
              <p className="text-3xl font-bold text-[#0A1F3A]">50K+</p>
              <p className="text-sm text-[#0A1F3A]/70">Lives Changed</p>
            </motion.div>
          </motion.div>

          {/* Right: Pillars */}
          <div className="grid sm:grid-cols-2 gap-4">
            {t.pillars.map((pillar, index) => {
              const Icon = pillar.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="p-5 rounded-2xl bg-white/[0.03] border border-white/10 hover:border-amber-500/30 transition-all"
                  data-testid={`pillar-card-${index}`}
                >
                  <Icon className="w-8 h-8 text-amber-400/70 mb-3" />
                  <h3 className="text-lg font-medium text-white/90 mb-2">{pillar.title}</h3>
                  <p className="text-sm text-white/50 font-light leading-relaxed">{pillar.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>

        {/* Quote */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-20 pt-16 border-t border-white/5"
        >
          <p className="text-xl md:text-2xl font-light italic text-white/60 max-w-2xl mx-auto">
            {t.quote}
          </p>
        </motion.div>
      </div>
    </section>
  );
};

export default MissionSection;
